/**
 * iter97 Phase A (RR + TT + UU) — Admin user detail slide-over.
 *
 * Read-only view of one student — account, case, timeline, cohort profile,
 * concierge lead, moments — plus append-only private admin notes and
 * suspend/reactivate actions. Every action goes through the backend audit
 * log; nothing is edited inline (that's Phase B, out of scope).
 *
 * Opens from either the Admin Users tab search results or the
 * "Who's arriving" recent-signups feed by passing a `userId`.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  X, Shield, Ban, RotateCcw, Eye, EyeOff, PlusCircle, Clock, Mail, Globe,
} from "lucide-react";

const TID = {
  drawer:       "admin-user-drawer",
  close:        "admin-user-drawer-close",
  statusPill:   "admin-user-status",
  revealWA:     "admin-user-reveal-whatsapp",
  notesList:    "admin-user-notes-list",
  noteInput:    "admin-user-note-input",
  noteSubmit:   "admin-user-note-submit",
  suspendBtn:   "admin-user-suspend-btn",
  reactivateBtn:"admin-user-reactivate-btn",
  confirmModal: "admin-user-action-confirm",
  confirmReason:"admin-user-action-reason",
  confirmFire:  "admin-user-action-fire",
  confirmCancel:"admin-user-action-cancel",
};

export default function UserDetailDrawer({ userId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState(null);
  const [err, setErr]   = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [action, setAction]     = useState(null);   // "suspend" | "reactivate" | null

  const load = useCallback(async (reveal = false) => {
    setErr(null);
    try {
      const r = await api.get(`/admin/users/${userId}${reveal ? "?reveal_whatsapp=1" : ""}`);
      setData(r.data);
      if (reveal) setRevealed(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load user.");
    }
  }, [userId]);

  const loadNotes = useCallback(async () => {
    try {
      const r = await api.get(`/admin/users/${userId}/notes`);
      setNotes(r.data);
    } catch { setNotes({ notes: [], count: 0 }); }
  }, [userId]);

  useEffect(() => { setData(null); setNotes(null); setRevealed(false); load(); loadNotes(); }, [userId, load, loadNotes]);

  const revealWa = async () => {
    if (revealed) return;
    if (!window.confirm("Reveal this user's WhatsApp number? This action is audit-logged.")) return;
    await load(true);
  };

  const addNote = async () => {
    const t = noteText.trim();
    if (t.length < 3) { toast.error("Note must be at least 3 characters."); return; }
    setNoteBusy(true);
    try {
      await api.post(`/admin/users/${userId}/notes`, { text: t });
      setNoteText("");
      await loadNotes();
      toast.success("Note saved to the running admin log.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note.");
    } finally { setNoteBusy(false); }
  };

  const fireAction = async (reason) => {
    if (!action) return;
    try {
      await api.post(`/admin/users/${userId}/${action}`, { reason });
      toast.success(action === "suspend" ? "Account suspended. Login is blocked." : "Account reactivated.");
      setAction(null);
      await load(revealed);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/60" data-testid={TID.drawer}>
      <div className="w-full max-w-2xl h-full bg-paper overflow-y-auto shadow-2xl">
        <div className="sticky top-0 z-10 bg-paper border-b border-ink/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted font-bold">User detail</div>
            <div className="text-lg font-bold text-ink">{data?.user?.name || (data ? "—" : "…")}</div>
          </div>
          <button onClick={onClose} data-testid={TID.close} className="text-muted hover:text-ink"><X size={20}/></button>
        </div>

        {err && <div className="m-6 moe-card p-4 text-sm text-[color:var(--moe-pink)]">{err}</div>}
        {!data && !err && <div className="m-6 moe-shimmer h-40"/>}

        {data && (
          <div className="p-6 space-y-8">
            <StatusBanner user={data.user}/>

            <Section title="Account" icon={Mail}>
              <Row label="Email" value={data.user.email}/>
              <Row label="Role" value={data.user.role}/>
              <Row label="Signed up" value={fmtDate(data.user.created_at)}/>
              <Row label="Signup source" value={data.user.source}/>
              <Row label="Institutional school" value={data.user.institutional_school_short || "—"}/>
              <Row label="Terms accepted" value={data.user.terms_accepted_at ? fmtDate(data.user.terms_accepted_at) : "—"}/>
              {data.user.suspended_at && (
                <Row label="Suspended at" value={fmtDate(data.user.suspended_at)}/>
              )}
              <Row label="WhatsApp" value={
                <div className="flex items-center gap-2">
                  <span className="tabular-nums">{revealed && data.whatsapp?.revealed ? data.whatsapp.revealed : (data.whatsapp?.masked || "—")}</span>
                  {data.whatsapp?.masked && !revealed && (
                    <button data-testid={TID.revealWA} onClick={revealWa}
                      className="moe-btn moe-btn-ghost !py-1 !px-2 text-[10px] uppercase tracking-widest">
                      <Eye size={12}/> Reveal
                    </button>
                  )}
                  {revealed && <span className="text-[10px] uppercase tracking-widest text-[color:var(--moe-pink)] font-bold flex items-center gap-1"><EyeOff size={10}/> Logged</span>}
                </div>
              }/>
            </Section>

            <Section title="Case & timeline" icon={Globe}>
              {data.case?.id ? (
                <>
                  <Row label="Home university" value={data.case.home_university}/>
                  <Row label="Host university" value={data.case.host_university}/>
                  <Row label="Host city · country" value={`${data.case.host_city || "—"} · ${data.case.country || "—"}`}/>
                  <Row label="Start · duration" value={`${data.case.start_date || "—"} · ${data.case.duration_days || "—"}d`}/>
                  <Row label="Tasks" value={`${data.timeline.tasks_done}/${data.timeline.tasks_total} (${data.timeline.pct}%)`}/>
                </>
              ) : <div className="text-sm text-muted italic">No case created yet.</div>}
            </Section>

            <Section title="Cohort profile">
              {data.cohort_profile?.opt_in_matching ? (
                <>
                  <Row label="Program" value={data.cohort_profile.program || "—"}/>
                  <Row label="Bio" value={<span className="whitespace-pre-wrap">{data.cohort_profile.bio || "—"}</span>}/>
                  <Row label="Last active" value={data.cohort_profile.last_active_at ? fmtDate(data.cohort_profile.last_active_at) : "—"}/>
                </>
              ) : <div className="text-sm text-muted italic">Not opted in to Cohort matching.</div>}
            </Section>

            {(data.moments_created?.length > 0 || data.moments_joined?.length > 0) && (
              <Section title="Moments">
                {data.moments_created?.length > 0 && (
                  <div className="text-sm">
                    <div className="text-[10px] uppercase tracking-widest text-muted font-bold mb-1">Created ({data.moments_created.length})</div>
                    <ul className="space-y-1">
                      {data.moments_created.map(m => (
                        <li key={m.id} className="text-[13px]">
                          <span className="font-semibold">{m.title}</span>
                          <span className="text-muted"> · {m.kind} · {m.status}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {data.moments_joined?.length > 0 && (
                  <div className="mt-3 text-sm">
                    <div className="text-[10px] uppercase tracking-widest text-muted font-bold mb-1">Joined ({data.moments_joined.length})</div>
                    <ul className="space-y-1">
                      {data.moments_joined.map(m => (
                        <li key={m.id} className="text-[13px]">
                          <span className="font-semibold">{m.title}</span>
                          <span className="text-muted"> · {m.kind}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Section>
            )}

            {data.concierge_lead && (
              <Section title="Concierge lead">
                <Row label="Status" value={data.concierge_lead.status}/>
                <Row label="Submitted" value={fmtDate(data.concierge_lead.created_at)}/>
              </Section>
            )}

            {/* TT — Private admin notes (append-only) */}
            <Section title="Private admin notes" icon={Shield}>
              <div className="text-[11px] text-muted mb-3">
                Append-only. Every entry is stamped with your admin email and cannot be edited or deleted.
              </div>
              <div className="space-y-2 mb-4" data-testid={TID.notesList}>
                {!notes ? <div className="moe-shimmer h-16"/> :
                  notes.count === 0 ? <div className="text-sm text-muted italic">No notes yet.</div> :
                  notes.notes.map(n => (
                    <div key={n.id} className="moe-card p-3 text-[13px]" data-testid={`admin-note-${n.id}`}>
                      <div className="text-[10px] uppercase tracking-widest text-muted font-bold">
                        {n.admin_email} · {fmtDate(n.created_at)}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap">{n.text}</div>
                    </div>
                  ))
                }
              </div>
              <textarea
                data-testid={TID.noteInput}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder="Add a support note… (min 3 chars, max 2000)"
                className="moe-input w-full text-sm"
                rows={3}
              />
              <div className="mt-2 flex justify-end">
                <button data-testid={TID.noteSubmit}
                        disabled={noteBusy || noteText.trim().length < 3}
                        onClick={addNote}
                        className={`moe-btn ${noteText.trim().length >= 3 ? "moe-btn-primary" : "moe-btn-ghost"}`}>
                  <PlusCircle size={14}/> {noteBusy ? "Saving…" : "Add note"}
                </button>
              </div>
            </Section>

            {/* UU — Suspend/Reactivate */}
            <Section title="Account actions" icon={Ban}>
              {data.user.deleted_at ? (
                <div className="text-sm text-muted italic">
                  Account is soft-deleted (purge scheduled). Suspension is not available.
                </div>
              ) : data.user.suspended_at ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm">
                    Login is currently <b>blocked</b>. Suspended {fmtDate(data.user.suspended_at)}.
                  </div>
                  <button data-testid={TID.reactivateBtn}
                          onClick={() => setAction("reactivate")}
                          className="moe-btn moe-btn-primary">
                    <RotateCcw size={14}/> Reactivate
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-4">
                  <div className="text-sm text-muted">
                    Suspend blocks login without deleting any data. Reversible at any time.
                  </div>
                  <button data-testid={TID.suspendBtn}
                          onClick={() => setAction("suspend")}
                          className="moe-btn moe-btn-ghost text-[color:var(--moe-pink)] border-[color:var(--moe-pink)]/40">
                    <Ban size={14}/> Suspend
                  </button>
                </div>
              )}
            </Section>
          </div>
        )}
      </div>

      {action && (
        <ActionConfirm
          action={action}
          userEmail={data?.user?.email}
          onClose={() => setAction(null)}
          onConfirm={fireAction}
        />
      )}
    </div>
  );
}

function StatusBanner({ user }) {
  const status = user.deleted_at ? "soft_deleted" : user.suspended_at ? "suspended" : "active";
  const map = {
    active:       { cls: "moe-panel-mint",  label: "Active" },
    suspended:    { cls: "moe-panel-pink",  label: "Suspended · login blocked" },
    soft_deleted: { cls: "moe-panel-yolk",  label: "Soft-deleted · pending purge" },
  }[status];
  return (
    <div className={`${map.cls} p-3 flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold`} data-testid="admin-user-status">
      <Clock size={12}/> {map.label}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="text-xs uppercase tracking-widest text-elec font-bold flex items-center gap-2 mb-3">
        {Icon && <Icon size={12}/>} {title}
      </div>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1.5 border-b border-ink/5 text-[13px]">
      <div className="text-[11px] uppercase tracking-widest text-muted font-semibold">{label}</div>
      <div className="col-span-2">{value ?? <span className="text-muted italic">—</span>}</div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toISOString().slice(0, 19).replace("T", " ") + " UTC"; }
  catch { return iso; }
}

function ActionConfirm({ action, userEmail, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy]     = useState(false);
  const isSuspend = action === "suspend";
  const submit = async () => {
    if (reason.trim().length < 3) return;
    setBusy(true);
    await onConfirm(reason.trim());
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" data-testid="admin-user-action-confirm">
      <div className="moe-card max-w-md w-full p-6 bg-paper">
        <div className="text-lg font-bold">
          {isSuspend ? "Suspend" : "Reactivate"} {userEmail}?
        </div>
        <div className="mt-1 text-[13px] text-ink/70 leading-snug">
          {isSuspend
            ? "This will block their login immediately. All data stays intact. The reason and your admin identity are stored in the audit log."
            : "This will restore login access. The reason and your admin identity are stored in the audit log."}
        </div>
        <div className="mt-4 text-[11px] uppercase tracking-widest text-muted font-bold">Reason (min 3 chars)</div>
        <textarea
          data-testid="admin-user-action-reason"
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder={isSuspend ? "e.g. reported for harassment, pending review" : "e.g. resolved, no violation confirmed"}
          className="mt-1 moe-input w-full text-sm"
          autoFocus
        />
        <div className="mt-5 flex items-center justify-end gap-3">
          <button data-testid="admin-user-action-cancel" onClick={onClose} className="moe-btn moe-btn-ghost">Cancel</button>
          <button
            data-testid="admin-user-action-fire"
            disabled={busy || reason.trim().length < 3}
            onClick={submit}
            className={`moe-btn ${reason.trim().length >= 3 ? (isSuspend ? "moe-btn-primary" : "moe-btn-primary") : "moe-btn-ghost"}`}
          >
            {busy ? "Working…" : isSuspend ? "Suspend account" : "Reactivate account"}
          </button>
        </div>
      </div>
    </div>
  );
}
